import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { legendSheetApi } from '../api';
import { queryKeys } from '../queries/keys';
import type { LineNumberFormat, TagFormat } from '../types';

/** Every legend sheet of one plant, cached under the plant so the page and the
 *  drawing setup's applicable legend options read the same list. */
export function useLegendSheets(domainId: string) {
  return useQuery({
    queryKey: queryKeys.legendSheets.list(domainId),
    queryFn: () => legendSheetApi.list(domainId),
    enabled: Boolean(domainId),
  });
}

/** A write names the plant it is for, so the plant the page shows when the
 *  answer arrives does not matter. */
type PlantWrite<Variables> = { domainId: string; variables: Variables };

/** Shared by every write: the server answers with the plant's full list.
 *
 *  The plant is captured when `mutate` is called. A page can switch plants while
 *  a write is in flight, and the mutation's callbacks read the hook's latest
 *  options, so the closure's `domainId` would then name the wrong plant. Writes
 *  to one plant run one at a time (`scope`), so two answers cannot land out of
 *  order, and a list read still in flight is cancelled before the answer is
 *  stored, so a stale read cannot land on top of it. */
function useLegendSheetWrite<Variables>(
  domainId: string,
  write: (domainId: string, variables: Variables) => ReturnType<typeof legendSheetApi.list>,
) {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, ...mutation } = useMutation({
    scope: { id: `legend-sheets:${domainId}` },
    mutationFn: (plantWrite: PlantWrite<Variables>) =>
      write(plantWrite.domainId, plantWrite.variables),
    onSuccess: async (sheets, plantWrite) => {
      const queryKey = queryKeys.legendSheets.list(plantWrite.domainId);
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, sheets);
    },
  });
  return {
    ...mutation,
    mutate: useCallback(
      (variables: Variables) => mutate({ domainId, variables }),
      [mutate, domainId],
    ),
    mutateAsync: useCallback(
      (variables: Variables) => mutateAsync({ domainId, variables }),
      [mutateAsync, domainId],
    ),
  };
}

export function useCreateLegendSheet(domainId: string) {
  return useLegendSheetWrite(
    domainId,
    (plant, sheet: { name: string; files: File[] }) =>
      legendSheetApi.create(plant, sheet),
  );
}

export function useUpdateLegendSheet(domainId: string) {
  return useLegendSheetWrite(
    domainId,
    (plant, variables: {
      sheetId: string;
      name: string;
      line_number_format: LineNumberFormat | null;
      tag_format: TagFormat | null;
    }) => {
      const { sheetId, ...update } = variables;
      return legendSheetApi.update(plant, sheetId, update);
    },
  );
}

export function useRemoveLegendSheet(domainId: string) {
  return useLegendSheetWrite(
    domainId,
    (plant, sheetId: string) => legendSheetApi.remove(plant, sheetId),
  );
}
